import React, { useState } from 'react';
import { createPortal } from 'react-dom';

const THEMES = {
  alert: {
    accent: '#b91c1c',
    bg: '#fecaca',
    bgChip: '#fee2e2',
    bgPanel: '#fef2f2',
    border: '#f87171',
    borderStrong: '#ef4444',
    iconBg: '#fca5a5',
    text: '#7f1d1d',
    textMuted: '#991b1b',
    shadow: '0 8px 28px rgba(220,38,38,.22), 0 2px 8px rgba(220,38,38,.12)'
  },
  warn: {
    accent: '#b45309',
    bg: '#fde68a',
    bgChip: '#fef3c7',
    bgPanel: '#fffbeb',
    border: '#fbbf24',
    borderStrong: '#f59e0b',
    iconBg: '#fcd34d',
    text: '#78350f',
    textMuted: '#92400e',
    shadow: '0 8px 28px rgba(217,119,6,.2), 0 2px 8px rgba(217,119,6,.1)'
  }
};

/**
 * Alerta de cola médica: `inline` en barra de herramientas o `floating` (fixed abajo a la derecha).
 */
export default function ColaMedicaFloatingAlert({
  resumen,
  onVerPrimero,
  bottomOffset = 56,
  variant = 'floating'
}) {
  const [open, setOpen] = useState(false);
  if (!resumen || resumen.alertCount <= 0) return null;

  const inline = variant === 'inline';
  const cautionCount = Number(resumen.caution || 0);
  const warningCount = Number(resumen.warning || 0);
  const criticalCount = Number(resumen.critical || 0);
  const prioridad = criticalCount + warningCount + cautionCount;
  const chipLabel = prioridad > 0
    ? `${prioridad} con demora`
    : 'Cola al día';

  const t = criticalCount > 0 ? THEMES.alert : THEMES.warn;

  const ui = (
    <div
      role="status"
      style={inline
        ? { position: 'relative', flexShrink: 0, zIndex: 130 }
        : {
          position: 'fixed',
          right: 16,
          bottom: bottomOffset,
          zIndex: 350,
          width: 'min(360px, calc(100vw - 32px))',
          pointerEvents: 'none'
        }}
    >
      <div style={inline ? undefined : { pointerEvents: 'auto' }}>
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          aria-expanded={open}
          aria-controls="cola-medica-alert-panel"
          style={{
            width: inline ? 'auto' : '100%',
            display: 'flex',
            alignItems: 'center',
            gap: inline ? 8 : 10,
            padding: inline ? '6px 12px' : '9px 14px',
            borderRadius: open
              ? (inline ? '12px 12px 4px 4px' : '14px 14px 6px 6px')
              : 999,
            border: `1.5px solid ${t.borderStrong}`,
            background: open ? t.bgPanel : t.bgChip,
            cursor: 'pointer',
            textAlign: 'left',
            boxShadow: t.shadow,
            transition: 'border-radius .25s ease, box-shadow .25s ease, background .2s ease',
            maxWidth: inline ? 'min(320px, 100%)' : undefined
          }}
        >
          <span
            style={{
              width: inline ? 26 : 30,
              height: inline ? 26 : 30,
              borderRadius: 999,
              background: t.iconBg,
              border: `1px solid ${t.border}`,
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: inline ? 14 : 15,
              flexShrink: 0
            }}
            aria-hidden
          >
            ⏱
          </span>
          <span style={{ flex: inline ? '0 1 auto' : 1, minWidth: 0 }}>
            <span style={{ display: 'block', fontSize: inline ? 12 : 13, fontWeight: 800, color: t.text, lineHeight: 1.2, whiteSpace: 'nowrap' }}>
              {chipLabel}
            </span>
            {!open && resumen.maxPaciente ? (
              <span
                style={{
                  display: 'block',
                  fontSize: 11,
                  color: t.textMuted,
                  fontWeight: 600,
                  marginTop: 2,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  maxWidth: inline ? 200 : undefined
                }}
              >
                {resumen.maxPaciente}
              </span>
            ) : null}
          </span>
          <span
            style={{
              fontSize: 12,
              fontWeight: 800,
              color: t.accent,
              transform: open
                ? (inline ? 'rotate(0deg)' : 'rotate(180deg)')
                : (inline ? 'rotate(-90deg)' : 'none'),
              transition: 'transform .28s ease',
              flexShrink: 0
            }}
            aria-hidden
          >
            {inline ? '▾' : '▴'}
          </span>
        </button>

        <div
          id="cola-medica-alert-panel"
          style={inline
            ? {
              position: 'absolute',
              top: 'calc(100% - 2px)',
              right: 0,
              left: 'auto',
              width: 'min(340px, calc(100vw - 24px))',
              transform: open ? 'translateY(0)' : 'translateY(-8px)',
              opacity: open ? 1 : 0,
              maxHeight: open ? 240 : 0,
              overflow: 'hidden',
              visibility: open ? 'visible' : 'hidden',
              transition: 'transform .34s cubic-bezier(.22,.9,.22,1), opacity .28s ease, max-height .34s cubic-bezier(.22,.9,.22,1), visibility .34s',
              borderRadius: 12,
              border: open ? `1.5px solid ${t.borderStrong}` : '1.5px solid transparent',
              background: t.bgPanel,
              boxShadow: open ? t.shadow : 'none',
              zIndex: 140
            }
            : {
              transform: open ? 'translateY(0)' : 'translateY(12px)',
              opacity: open ? 1 : 0,
              maxHeight: open ? 220 : 0,
              overflow: 'hidden',
              visibility: open ? 'visible' : 'hidden',
              transition: 'transform .34s cubic-bezier(.22,.9,.22,1), opacity .28s ease, max-height .34s cubic-bezier(.22,.9,.22,1), visibility .34s',
              borderRadius: '0 0 14px 14px',
              border: open ? `1.5px solid ${t.borderStrong}` : '1.5px solid transparent',
              borderTop: 'none',
              background: t.bgPanel,
              marginTop: -4,
              boxShadow: open ? t.shadow : 'none'
            }}
        >
          <div style={{ padding: '12px 14px 14px' }}>
            <div style={{ fontSize: 13, fontWeight: 800, color: t.text, lineHeight: 1.4, marginBottom: 6 }}>
              {prioridad > 0
                ? `${prioridad} pedido${prioridad === 1 ? '' : 's'} llevan mucho tiempo esperando`
                : 'Hay pedidos en cola dentro del plazo'}
            </div>
            {resumen.maxPaciente && resumen.maxDurationLabel ? (
              <div style={{ fontSize: 12, color: t.textMuted, fontWeight: 600, lineHeight: 1.45, marginBottom: 8 }}>
                Primero: <strong style={{ color: t.text }}>{resumen.maxPaciente}</strong>
                {' · '}
                <span style={{ color: t.accent, fontWeight: 800 }}>{resumen.maxDurationLabel}</span>
              </div>
            ) : null}
            <div style={{ fontSize: 11, color: t.textMuted, opacity: 0.85, marginBottom: 10 }}>
              Orden: del más antiguo al más reciente.
            </div>
            {typeof onVerPrimero === 'function' ? (
              <button
                type="button"
                onClick={() => {
                  onVerPrimero();
                  setOpen(false);
                }}
                style={{
                  padding: '7px 12px',
                  borderRadius: 8,
                  border: `1px solid ${t.border}`,
                  background: t.bg,
                  color: t.text,
                  fontSize: 12,
                  fontWeight: 800,
                  cursor: 'pointer'
                }}
              >
                Ir al primero
              </button>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );

  if (inline) return ui;
  if (typeof document === 'undefined') return null;
  return createPortal(ui, document.body);
}
